import {
  httpClient,
  fetchClientToken,
  fetchLoyaltyToken,
  getPersistedToken,
  persistToken,
  resetToken,
  type Token,
} from "@koala/sdk/v4";
import {
  BUILD_VERSION,
  ENV,
  clientId,
  clientSecret,
} from "@/constants/envConfig";
import { getCachedToken, setCachedToken } from "@/services/token-cache";
import {
  getAndroidShellDeviceId,
  getAndroidShellVersion,
} from "@/utils/android";

interface ClientOrigin {
  origin: string;
}

const inFlightTokens = new Map<string, Promise<Token>>();

export function createHttpClient({ origin }: ClientOrigin) {
  const androidDeviceId = getAndroidShellDeviceId();
  const androidVersion = getAndroidShellVersion();
  const persistedToken = getPersistedToken({ origin });
  const cachedToken = persistedToken ? null : getCachedToken(origin);
  return httpClient({
    credentials: { clientId, clientSecret },
    env: ENV,
    headers: {
      ...(androidDeviceId && { androidDeviceId }),
      ...(androidVersion && { androidVersion }),
      serviceLabel: `Koala Web Ordering / ${BUILD_VERSION}`,
      ...(cachedToken && {
        Authorization: `Bearer ${cachedToken.access_token}`,
      }),
    },
    origin,
  });
}

export async function getClientToken({ origin }: ClientOrigin) {
  const androidDeviceId = getAndroidShellDeviceId();
  const androidVersion = getAndroidShellVersion();
  return fetchClientToken({
    credentials: { clientId, clientSecret },
    env: ENV,
    headers: {
      ...(androidDeviceId && { androidDeviceId }),
      ...(androidVersion && { androidVersion }),
      serviceLabel: `Koala Web Ordering / ${BUILD_VERSION}`,
    },
    origin,
  });
}

export async function primeClientToken({
  origin,
}: ClientOrigin): Promise<Token> {
  const persistedToken = getPersistedToken({ origin });
  if (persistedToken) {
    setCachedToken(origin, persistedToken);
    return persistedToken;
  }
  const cachedToken = getCachedToken(origin);
  if (cachedToken) {
    return cachedToken;
  }
  const inflight = inFlightTokens.get(origin);
  if (inflight) {
    return inflight;
  }

  const androidDeviceId = getAndroidShellDeviceId();
  const androidVersion = getAndroidShellVersion();
  const tokenPromise = fetchClientToken({
    credentials: { clientId, clientSecret },
    env: ENV,
    headers: {
      ...(androidDeviceId && { androidDeviceId }),
      ...(androidVersion && { androidVersion }),
      serviceLabel: `Koala Web Ordering / ${BUILD_VERSION}`,
    },
    origin,
  })
    .then((token) => {
      setCachedToken(origin, token);
      persistToken({ origin, token });
      return token;
    })
    .finally(() => {
      inFlightTokens.delete(origin);
    });

  inFlightTokens.set(origin, tokenPromise);
  return tokenPromise;
}

export async function getLoyaltyToken(
  payload: Record<string, any>,
  { origin }: ClientOrigin
) {
  const androidDeviceId = getAndroidShellDeviceId();
  const androidVersion = getAndroidShellVersion();
  return fetchLoyaltyToken({
    credentials: { clientId, clientSecret },
    env: ENV,
    headers: {
      ...(androidDeviceId && { androidDeviceId }),
      ...(androidVersion && { androidVersion }),
      serviceLabel: `Koala Web Ordering / ${BUILD_VERSION}`,
    },
    origin,
    payload,
  });
}

export function resetLoyaltyToken({ origin }: ClientOrigin) {
  const androidDeviceId = getAndroidShellDeviceId();
  const androidVersion = getAndroidShellVersion();

  return resetToken({
    credentials: { clientId, clientSecret },
    env: ENV,
    headers: {
      ...(androidDeviceId && { androidDeviceId }),
      ...(androidVersion && { androidVersion }),
      serviceLabel: `Koala Web Ordering / ${BUILD_VERSION}`,
    },
    origin,
  });
}
